import React from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PaginationProps {
  /**
   * Current page number (1-indexed)
   */
  currentPage: number;

  /**
   * Total number of pages
   */
  totalPages: number;

  /**
   * Callback when page changes
   */
  onPageChange: (page: number) => void;

  /**
   * Total number of items (optional, for display purposes)
   */
  total?: number;

  /**
   * Number of items per page (optional, for display purposes)
   */
  pageSize?: number;

  /**
   * Custom class name for container
   */
  className?: string;

  /**
   * Show page info text (e.g., "Page 1 of 10")
   * @default true
   */
  showPageInfo?: boolean;

  /**
   * Show range info text (e.g., "Showing 1-10 of 100")
   * @default false
   */
  showRangeInfo?: boolean;

  /**
   * Custom range info text
   */
  rangeInfoText?: string;

  /**
   * Button variant
   * @default "outline"
   */
  buttonVariant?: 'default' | 'outline' | 'ghost' | 'link';

  /**
   * Button size
   * @default "sm"
   */
  buttonSize?: 'sm' | 'default' | 'lg';

  /**
   * Always show pagination info even when there's only 1 page
   * @default false
   */
  alwaysShowInfo?: boolean;
}

/**
 * Pagination - Reusable pagination controls
 *
 * Provides consistent pagination UI across the application with previous/next
 * navigation, page info display, and optional range display.
 *
 * @example
 * // Basic usage
 * <Pagination
 *   currentPage={page}
 *   totalPages={totalPages}
 *   onPageChange={setPage}
 * />
 *
 * @example
 * // With range info
 * <Pagination
 *   currentPage={page}
 *   totalPages={totalPages}
 *   total={total}
 *   pageSize={pageSize}
 *   onPageChange={setPage}
 *   showRangeInfo
 * />
 *
 * @example
 * // Custom styling
 * <Pagination
 *   currentPage={page}
 *   totalPages={totalPages}
 *   onPageChange={setPage}
 *   buttonVariant="default"
 *   buttonSize="default"
 *   className="border-t pt-6"
 * />
 */
export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  total,
  pageSize,
  className,
  showPageInfo = true,
  showRangeInfo = false,
  rangeInfoText,
  buttonVariant = 'outline',
  buttonSize = 'sm',
  alwaysShowInfo = false,
}) => {
  // Don't render if there's only one page or no pages (unless alwaysShowInfo is true)
  if (totalPages <= 1 && !alwaysShowInfo) {
    return null;
  }

  // If only 1 page and alwaysShowInfo, show info but hide navigation buttons
  const showNavigation = totalPages > 1;

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  // Calculate range info
  const getRangeInfo = () => {
    if (rangeInfoText) return rangeInfoText;
    if (!total || !pageSize) return null;

    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, total);

    // Grouped: hub totals run to five and six figures, and the counts these
    // pages show elsewhere (tab pills, header badge) are already grouped.
    return `Showing ${start.toLocaleString()}-${end.toLocaleString()} of ${total.toLocaleString()}`;
  };

  return (
    <div className={cn('flex items-center justify-between', className)}>
      {/* Left side - Range info or page info */}
      <div className="text-sm text-slate-600">
        {showRangeInfo && getRangeInfo() ? (
          <span>{getRangeInfo()}</span>
        ) : showPageInfo ? (
          <span>
            Page {currentPage} of {totalPages}
          </span>
        ) : (
          <span /> // Empty span to maintain flex layout
        )}
      </div>

      {/* Right side - Navigation buttons (only show if multiple pages) */}
      {showNavigation && (
        <div className="flex gap-2">
          <Button
            variant={buttonVariant}
            size={buttonSize}
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="px-2 xs:px-3"
          >
            <ChevronLeft className="h-4 w-4 xs:mr-1" />
            <span className="hidden xs:inline">Previous</span>
          </Button>
          <Button
            variant={buttonVariant}
            size={buttonSize}
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="px-2 xs:px-3"
          >
            <span className="hidden xs:inline">Next</span>
            <ChevronRight className="h-4 w-4 xs:ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * SimplePagination - Minimal pagination with just buttons
 *
 * Use when you only need Previous/Next buttons without page info display.
 *
 * @example
 * <SimplePagination
 *   currentPage={page}
 *   totalPages={totalPages}
 *   onPageChange={setPage}
 * />
 */
export const SimplePagination: React.FC<
  Omit<PaginationProps, 'showPageInfo' | 'showRangeInfo'>
> = (props) => {
  return <Pagination {...props} showPageInfo={false} showRangeInfo={false} />;
};

interface NumberedPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
  /**
   * Maximum number of page buttons to show
   * @default 5
   */
  maxVisiblePages?: number;
  /**
   * Always show pagination even when there's only 1 page
   * @default true
   */
  alwaysShow?: boolean;
}

/**
 * NumberedPagination - Pagination with numbered page buttons
 *
 * Displays page numbers (e.g., 1, 2, 3, 4, 5) with left/right arrows.
 * Current page is highlighted with a blue border.
 *
 * @example
 * <NumberedPagination
 *   currentPage={page}
 *   totalPages={totalPages}
 *   onPageChange={setPage}
 * />
 */
export const NumberedPagination: React.FC<NumberedPaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  className,
  maxVisiblePages = 5,
  alwaysShow = true,
}) => {
  // Don't render if there are no pages, or if only 1 page and alwaysShow is false
  if (totalPages <= 0 || (totalPages === 1 && !alwaysShow)) {
    return null;
  }

  // Ensure at least 1 page
  const effectiveTotalPages = Math.max(1, totalPages);

  // Calculate which page numbers to show
  const getVisiblePages = (): number[] => {
    if (effectiveTotalPages <= maxVisiblePages) {
      return Array.from({ length: effectiveTotalPages }, (_, i) => i + 1);
    }

    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, currentPage - half);
    const end = Math.min(effectiveTotalPages, start + maxVisiblePages - 1);

    // Adjust start if we're near the end
    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const visiblePages = getVisiblePages();

  return (
    <div className={cn('flex items-center justify-center gap-1', className)}>
      {/* Previous button */}
      <button
        type="button"
        aria-label="Previous page"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={cn(
          'w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 bg-white transition-colors',
          currentPage === 1
            ? 'text-slate-300 cursor-not-allowed'
            : 'text-slate-600 hover:bg-slate-50 hover:border-slate-300'
        )}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      {/* Page number buttons */}
      {visiblePages.map((pageNum) => (
        <button
          key={pageNum}
          type="button"
          aria-label={`Page ${pageNum}`}
          aria-current={pageNum === currentPage ? 'page' : undefined}
          onClick={() => onPageChange(pageNum)}
          className={cn(
            'w-9 h-9 flex items-center justify-center rounded-lg border text-sm font-medium transition-colors',
            pageNum === currentPage
              ? 'border-blue-500 text-blue-600 bg-white'
              : 'border-slate-200 text-slate-600 bg-white hover:bg-slate-50 hover:border-slate-300'
          )}
        >
          {pageNum}
        </button>
      ))}

      {/* Next button */}
      <button
        type="button"
        aria-label="Next page"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === effectiveTotalPages}
        className={cn(
          'w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 bg-white transition-colors',
          currentPage === effectiveTotalPages
            ? 'text-slate-300 cursor-not-allowed'
            : 'text-slate-600 hover:bg-slate-50 hover:border-slate-300'
        )}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};
